import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, FileText, Loader, Send, Smartphone } from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  createOrGetDeliveryConversation,
  fetchConversationMessages,
  fetchDriverCustomerChannelStatus,
  hasCommunicationMedia,
  markConversationRead,
  sendDriverCustomerMessage,
} from '../shared/communication/api';
import MessageBubble from '../shared/communication/MessageBubble';
import MediaMessageControls from '../shared/communication/MediaMessageControls';
import { socket, socketAuthPayload } from '../admin/chat/socket';

export default function DriverDeliveryConversationChat({ delivery, driverId, onBackClick }) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState('fastmax_app');
  const [channelStatus, setChannelStatus] = useState(null);
  const [content, setContent] = useState('');
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  const conversationId = conversation?.id;
  const deliveryId = delivery?.id;
  const whatsappAvailable = Boolean(channelStatus?.whatsapp_available);
  const whatsappDisabledReason = channelStatus?.whatsapp_disabled_reason || 'WhatsApp no disponible. Usa Cliente App.';

  const load = useCallback(async () => {
    if (!deliveryId) return;
    setIsLoading(true);
    setError(null);
    try {
      const nextConversation = await createOrGetDeliveryConversation(deliveryId, {
        created_by_user_id: driverId,
        viewer_type: 'driver',
      });
      setConversation(nextConversation);
      const [messagesData, statusData] = await Promise.all([
        fetchConversationMessages(nextConversation.id, {
          currentUserType: 'driver',
          currentUserId: driverId,
        }),
        fetchDriverCustomerChannelStatus(nextConversation.id),
      ]);
      setMessages(messagesData.messages || []);
      setChannelStatus(statusData || null);
      if (!statusData?.whatsapp_available) setChannel('fastmax_app');
      await markConversationRead(nextConversation.id, {
        recipient_type: 'driver',
        user_id: driverId,
        driver_id: driverId,
      });
    } catch (err) {
      console.error('Driver delivery conversation error:', err);
      setError('No se pudo cargar el chat de esta entrega.');
    } finally {
      setIsLoading(false);
    }
  }, [deliveryId, driverId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!conversationId) return undefined;
    const roomPayload = socketAuthPayload({ conversation_id: conversationId });
    socket.emit('join_conversation', roomPayload);

    const handleMessage = (eventPayload) => {
      const message = eventPayload?.message || eventPayload;
      if (Number(message?.conversation_id) !== Number(conversationId)) return;
      setMessages(prev => {
        if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
        return [...prev, message];
      });
    };
    const handleConversationUpdated = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.conversation) setConversation(payload.conversation);
    };
    const handleConnect = () => {
      socket.emit('join_conversation', roomPayload);
      load();
    };

    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_updated', handleConversationUpdated);
    socket.on('conversation_read_updated', handleConversationUpdated);
    socket.on('connect', handleConnect);
    return () => {
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_updated', handleConversationUpdated);
      socket.off('conversation_read_updated', handleConversationUpdated);
      socket.off('connect', handleConnect);
      socket.emit('leave_conversation', roomPayload);
    };
  }, [conversationId, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSubmit(event) {
    event.preventDefault();
    const text = content.trim();
    if ((!text && !mediaAttachment) || !conversationId || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const selectedChannel = channel === 'whatsapp' && whatsappAvailable ? 'whatsapp' : 'fastmax_app';
      const payload = {
        channel: selectedChannel,
        content: text,
        message_type: mediaAttachment?.type || 'text',
        media_file: mediaAttachment?.file,
        media_type: mediaAttachment?.type,
      };
      const data = await sendDriverCustomerMessage(
        conversationId,
        hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
      );
      if (data.message) {
        setMessages(prev => (
          prev.some(item => item.id === data.message.id && item.channel === data.message.channel)
            ? prev
            : [...prev, data.message]
        ));
      }
      setContent('');
      setMediaAttachment(null);
      if (selectedChannel === 'whatsapp') {
        const statusData = await fetchDriverCustomerChannelStatus(conversationId);
        setChannelStatus(statusData || null);
      }
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.response?.data?.error || err.message;
      if (status === 428) {
        setChannel('fastmax_app');
        setChannelStatus(prev => ({
          ...(prev || {}),
          whatsapp_available: false,
          whatsapp_disabled_reason: message || 'Ventana WhatsApp cerrada. Usa Cliente App.',
        }));
      }
      setError(message || 'No se pudo enviar el mensaje.');
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (!content.trim() && !mediaAttachment) return;
    handleSubmit(event);
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <button onClick={onBackClick} className="rounded-lg bg-slate-100 p-2 text-slate-700 hover:bg-slate-200">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h2 className="truncate text-base font-extrabold text-slate-950">Chat entrega #{deliveryId}</h2>
          <p className="truncate text-xs text-slate-500">{delivery?.recipient_name || 'Cliente'} ? {delivery?.recipient_phone || ''}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2">
        <button
          type="button"
          onClick={() => setChannel('fastmax_app')}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold ${channel === 'fastmax_app' ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-700'}`}
        >
          <FileText className="h-4 w-4" />
          Cliente App
        </button>
        <button
          type="button"
          onClick={() => whatsappAvailable && setChannel('whatsapp')}
          disabled={!whatsappAvailable || isSending}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 ${channel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'}`}
          title={whatsappAvailable ? 'Enviar por WhatsApp' : whatsappDisabledReason}
        >
          <Smartphone className="h-4 w-4" />
          WhatsApp
        </button>
        {!whatsappAvailable && (
          <span className="text-[11px] font-semibold text-amber-700">{whatsappDisabledReason}</span>
        )}
      </div>

      {error && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <div className="flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" /> Aviso</div>
          <div>{error}</div>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
            <Loader className="h-4 w-4 animate-spin" /> Cargando chat...
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Sin mensajes todavia. Este hilo unifica Cliente App, WhatsApp y coordinacion operativa.
          </div>
        )}
        {messages.map(message => (
          <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType="driver" />
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSubmit} className="border-t border-slate-200 bg-white p-3">
        <div className="mb-2 text-[11px] text-slate-500">
          {channel === 'whatsapp'
            ? 'Mensaje, imagen o audio al WhatsApp del cliente final. Audio WhatsApp requiere OGG/OPUS.'
            : 'Mensaje, imagen o audio visible para el cliente final dentro de Fast Max App y registrado en esta entrega.'}
        </div>
        <div className="mb-2">
          <MediaMessageControls
            value={mediaAttachment}
            onChange={setMediaAttachment}
            disabled={isSending || isLoading}
            compact
          />
        </div>

        <div className="flex gap-2">
          <textarea
            value={content}
            onChange={event => setContent(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            placeholder={channel === 'whatsapp' ? 'Escribe al WhatsApp del cliente...' : 'Escribe al cliente final...'}
            className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
          />
          <button
            type="submit"
            disabled={isSending || isLoading || (!content.trim() && !mediaAttachment)}
            className="self-end inline-flex items-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-bold text-white hover:bg-[#9d2d62] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSending ? <Loader className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar
          </button>
        </div>
      </form>
    </div>
  );
}
